import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Save, FileText, Download, CheckCircle2 } from 'lucide-react';

/**
 * Save-quote modal — generates the chosen quote PDF, writes it to the
 * server-side Quote Archive, and immediately streams a copy to the user's
 * browser so they can pick a hard-drive folder via the OS save dialog.
 *
 * Email send is intentionally NOT wired here — that comes later once the
 * full Communication Scripts library is finished. For now we just log
 * the action against the appraisal so the user has a paper trail.
 */
export default function SaveQuoteModal({ open, onClose, appraisalId, clientEmail, onSaved }) {
  const { api } = useAuth();
  const [reportType, setReportType] = useState('quotation');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);
  const [error, setError] = useState('');

  if (!open) return null;

  const save = async () => {
    setSaving(true); setError(''); setSavedRecord(null);
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/archive-quote`, {
        report_type: reportType,
        note,
      });
      setSavedRecord(data);
      // Immediately stream the file so the user can pick a hard-drive folder.
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/quote-archives/${data._id}/download`;
        const r = await fetch(url, { credentials: 'include' });
        const blob = await r.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = data.filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
      } catch { /* download is a nice-to-have; the file is safely archived */ }
      onSaved && onSaved(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const close = () => {
    setSavedRecord(null); setNote(''); setError('');
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
      <div data-testid="save-quote-modal" className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#D35400]/10 flex items-center justify-center"><FileText size={16} className="text-[#D35400]" /></div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Save quote to archive</h2>
              <p className="text-[11px] text-slate-500">Stores a permanent copy on the server and downloads one to your computer.</p>
            </div>
          </div>
          <button onClick={close} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
        </div>

        {!savedRecord ? (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 block mb-2">Quote type</label>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { v: 'quotation', label: 'Basic Quotation', sub: 'Line-items + totals — typical 1-2 page customer quote.' },
                  { v: 'quotation-detailed', label: 'Detailed Quotation', sub: 'Full letter with payment terms + acceptance block.' },
                ].map(o => (
                  <label
                    key={o.v}
                    data-testid={`save-quote-type-${o.v}`}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportType === o.v ? 'border-[#D35400] bg-[#FFF7ED]/60' : 'border-[#E2E2DF] hover:border-slate-300'}`}
                  >
                    <input
                      type="radio"
                      name="reportType"
                      value={o.v}
                      checked={reportType === o.v}
                      onChange={() => setReportType(o.v)}
                      className="mt-0.5 accent-[#D35400]"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{o.label}</p>
                      <p className="text-[11px] text-slate-500">{o.sub}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 block mb-1">Note (optional)</label>
              <input
                data-testid="save-quote-note"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. Final version sent to client, signed copy returned…"
                className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
              />
            </div>

            <div className="bg-amber-50/60 border border-amber-200 rounded-md p-3">
              <p className="text-[11px] text-amber-800 leading-relaxed">
                <span className="font-semibold">Heads up:</span> automatic email send is paused while we finish the Communication Scripts library. For now this saves a permanent server copy <span className="font-semibold">and</span> downloads a copy to your computer. {clientEmail && <span>You can email it to <span className="font-mono-num">{clientEmail}</span> manually.</span>}
              </p>
            </div>

            {error && <p data-testid="save-quote-error" className="text-xs text-red-600">{error}</p>}
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold">
              <CheckCircle2 size={16} /> Quote archived
            </div>
            <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-3 text-xs">
              <p className="font-mono-num font-semibold text-slate-800 break-all">{savedRecord.filename}</p>
              <p className="text-slate-500 mt-1">{(savedRecord.size_bytes / 1024).toFixed(0)} KB · saved {new Date(savedRecord.created_at).toLocaleString('en-NZ')}</p>
            </div>
            <p className="text-[12px] text-slate-600">A copy was also downloaded to your computer (check your browser's Downloads folder, then drag to the hard-drive folder of your choice).</p>
          </div>
        )}

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
          {savedRecord ? (
            <button onClick={close} data-testid="save-quote-done-btn" className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000]">Done</button>
          ) : (
            <>
              <button onClick={close} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
              <button data-testid="save-quote-confirm-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] disabled:opacity-50">
                {saving ? <Save size={14} className="animate-pulse" /> : <Download size={14} />}
                {saving ? 'Saving…' : 'Save & download'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
